import Animated, {
  Extrapolate,
  interpolate,
  useAnimatedKeyboard,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import React from 'react';
import { BrandPageFieldsFragment } from '@/types';
import { BrandPageImageHeader } from './brand-page-image-header';
import { BrandStorefrontModule } from '@/utils/brand-page';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createHeaderShadow } from '@/components/navigation-header-content';
import { createStyles } from '@/contexts/theme-context';
import { HeaderBackButton } from '@/components/header-back-button';
import { SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import {
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const MOBILE_IMAGE_HEIGHT = 270;
const MAIN_LOGO_SIZE = 104;
const STICKY_HEADER_HEIGHT = 56;
const STICKY_HEADER_LOGO_SIZE = 40;

interface AnimatedScrollViewProps {
  ListHeaderComponent?: React.ReactNode;
  onScroll: ScrollViewProps['onScroll'];
  scrollViewAnimatedStyle?: StyleProp<ViewStyle>;
}

interface Props {
  brandPageContent?: BrandPageFieldsFragment | null;
  children: (
    animatedScrollViewProps: AnimatedScrollViewProps,
  ) => React.ReactNode;
}

export const BrandPageHeader: React.FC<Props> = ({
  brandPageContent,
  children,
}) => {
  const storefrontModule: BrandStorefrontModule | null =
    brandPageContent?.storefront_module;
  const { goBack } = useNavigation();
  const { top } = useSafeAreaInsets();
  const [styles] = useStyles();

  const scrollY = useSharedValue(0);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  const stickyHeaderBeginY = MOBILE_IMAGE_HEIGHT - STICKY_HEADER_HEIGHT - top;
  const stickyHeaderEndY = stickyHeaderBeginY + MAIN_LOGO_SIZE / 2;

  const mainHeaderStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        scrollY.value,
        [0, stickyHeaderEndY],
        [1, 0],
        Extrapolate.CLAMP,
      ),
    };
  });

  const hasImages = Boolean(
    brandPageContent?.logo_image_url && storefrontModule?.mobile_image_url,
  );

  const stickyHeaderStyle = useAnimatedStyle(() => {
    return {
      opacity: hasImages
        ? interpolate(
            scrollY.value,
            [stickyHeaderBeginY, stickyHeaderEndY],
            [0, 1],
            Extrapolate.CLAMP,
          )
        : 1,
    };
  });

  const keyboard = useAnimatedKeyboard();
  const keyboardStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: -keyboard.height.value }],
  }));

  return (
    <View style={styles.base}>
      {children({
        ListHeaderComponent: brandPageContent ? (
          <Animated.View
            style={[
              hasImages ? null : { height: STICKY_HEADER_HEIGHT + top },
              mainHeaderStyle,
            ]}
          >
            <BrandPageImageHeader brandPageContent={brandPageContent} />
          </Animated.View>
        ) : null,
        onScroll: scrollHandler,
        scrollViewAnimatedStyle: keyboardStyle,
      })}
      <View style={[styles.stickyHeader, { paddingTop: top }]}>
        <Animated.View
          style={[
            styles.stickyHeaderBackground,
            brandPageContent ? styles.stickyHeaderShadow : null,
            stickyHeaderStyle,
          ]}
        >
          {brandPageContent?.logo_image_url ? (
            <CachedImage
              alt={brandPageContent?.brand_name ?? ''}
              contentFit="contain"
              source={{
                uri: brandPageContent?.logo_image_url,
              }}
              style={styles.stickyHeaderLogoImage}
              testID="brand-page-sticky-header-logo-image"
            />
          ) : (
            <Text style={[TYPOGRAPHY.h3, styles.stickyHeaderBrandName]}>
              {brandPageContent?.brand_name}
            </Text>
          )}
        </Animated.View>
        <HeaderBackButton
          containerStyle={styles.backButtonContainer}
          onPress={goBack}
        />
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  backButtonContainer: {
    marginLeft: SCREEN_PADDING_SMALL,
  },
  base: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
  stickyHeader: {
    left: 0,
    paddingBottom: 4,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  stickyHeaderBackground: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    justifyContent: 'flex-end',
    paddingBottom: 8,
  },
  stickyHeaderBrandName: {
    paddingBottom: 8,
  },
  stickyHeaderLogoImage: {
    height: STICKY_HEADER_LOGO_SIZE,
    width: STICKY_HEADER_LOGO_SIZE,
  },
  stickyHeaderShadow: {
    ...createHeaderShadow(theme),
  },
}));
